import { useEffect, useState } from "react";
import { api, apiError, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FileText, Download, Pencil, X, Send } from "lucide-react";
import { toast } from "sonner";

const DRAFT_ROLES = ["aide", "sped_teacher", "supervisor", "tag_admin"];
const SUBMIT_ROLES = ["sped_teacher", "supervisor", "tag_admin"];
const FIELDS = [
  ["subject_area", "Subject Area", "input"],
  ["class_period", "Class Period", "input"],
  ["current_grade", "Current Grade", "input"],
  ["strengths", "Areas of Strength", "textarea"],
  ["involvement_progress", "Involvement and Progress Toward the Goal", "textarea"],
  ["accommodations", "Accommodations/Modifications Used", "textarea"],
  ["suggestions", "Suggestions", "textarea"],
  ["comments", "Additional Comments", "textarea"],
];
const inputCls = "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600";

export const TeacherReportPanel = ({ student, onClose }) => {
  const { user } = useAuth();
  const canDraft = DRAFT_ROLES.includes(user?.role);
  const canSubmit = SUBMIT_ROLES.includes(user?.role);
  const [month, setMonth] = useState(new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" }).slice(0, 7));
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/teacher-reports?student_id=${student.id}&month=${month}`).then((r) => setReports(r.data)).catch(() => setReports([]));
  useEffect(() => { load(); }, [student.id, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const newDraft = async () => {
    try {
      const r = await api.get(`/teacher-reports/prefill?student_id=${student.id}&month=${month}`);
      setForm({ subject_area: "", class_period: "", current_grade: "", strengths: "", involvement_progress: r.data.involvement_progress, accommodations: "", suggestions: "", comments: "" });
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, student_id: student.id, month };
      if (form.id) await api.put(`/teacher-reports/${form.id}`, payload);
      else await api.post("/teacher-reports", payload);
      toast.success(form.id ? "Draft updated" : "Draft saved");
      setForm(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const submit = async (r) => {
    if (!window.confirm("Review and submit this teacher report? It will be locked after submission.")) return;
    try {
      await api.post(`/teacher-reports/${r.id}/submit`);
      toast.success("Report submitted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const download = (r) => {
    downloadFile(`/teacher-reports/${r.id}/pdf`, `${student.last_name}, ${student.first_name} – Teacher Report – ${r.month}.pdf`).catch((e) => toast.error(apiError(e)));
  };

  const canEdit = (r) => r.status === "draft" && (user.role !== "aide" || r.drafted_by === user.id) && canDraft;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 sf-overlay flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="sf-sheet bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><FileText size={17} /> Monthly Teacher Report — {student.first_name} {student.last_name}</h3>
          <button data-testid="teacher-report-close-btn" onClick={onClose} className="p-1.5 text-[#667085] hover:text-[#132238] rounded-lg"><X size={17} /></button>
        </div>

        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500">Month</label>
            <input data-testid="teacher-report-month-input" type="month" value={month} onChange={(e) => { setMonth(e.target.value); setForm(null); }} className={inputCls} />
          </div>
          {canDraft && !form && (
            <button data-testid="new-teacher-report-btn" onClick={newDraft}
              className="text-sm font-semibold bg-[#087F5B] hover:bg-[#066548] text-white px-4 py-2.5 rounded-[10px] transition-colors duration-150">New draft</button>
          )}
        </div>

        {!form && (
          <div className="space-y-2">
            {reports.length === 0 && <p data-testid="no-teacher-reports" className="text-sm text-[#667085]">No teacher reports for this month yet.</p>}
            {reports.map((r) => (
              <div key={r.id} data-testid={`teacher-report-row-${r.id}`} className="border border-[#E5E9EE] rounded-xl p-3.5 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">{r.subject_area || "Teacher report"} · {r.month}</p>
                  <p className="text-xs text-slate-500">Drafted by {r.drafted_by_name}{r.status === "submitted" ? ` · Submitted by ${r.submitted_line} on ${r.submitted_at}` : ""}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${r.status === "submitted" ? "text-emerald-800 bg-emerald-50 border-emerald-200" : "text-amber-800 bg-amber-50 border-amber-200"}`}>
                  {r.status === "submitted" ? "SUBMITTED" : "DRAFT"}
                </span>
                {canEdit(r) && (
                  <button data-testid={`edit-teacher-report-btn-${r.id}`} onClick={() => setForm({ ...r })}
                    className="flex items-center gap-1 text-xs font-semibold text-[#667085] hover:text-[#132238] px-2.5 py-1.5 rounded-lg hover:bg-slate-50"><Pencil size={12} /> Edit</button>
                )}
                {r.status === "draft" && canSubmit && (
                  <button data-testid={`submit-teacher-report-btn-${r.id}`} onClick={() => submit(r)}
                    className="flex items-center gap-1 text-xs font-semibold text-white bg-[#087F5B] hover:bg-[#066548] px-3 py-1.5 rounded-lg"><Send size={12} /> Review &amp; submit</button>
                )}
                <button data-testid={`download-teacher-report-btn-${r.id}`} onClick={() => download(r)}
                  className="flex items-center gap-1 text-xs font-semibold text-[#087F5B] bg-[#EAF8F3] hover:bg-[#d9f2e8] px-3 py-1.5 rounded-lg"><Download size={12} /> PDF</button>
              </div>
            ))}
          </div>
        )}

        {form && (
          <div className="space-y-3">
            {FIELDS.map(([key, lbl, kind]) => (
              <div key={key}>
                <label className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500">{lbl}</label>
                {kind === "input" ? (
                  <input data-testid={`tr-${key}-input`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={inputCls} />
                ) : (
                  <textarea data-testid={`tr-${key}-input`} rows={key === "involvement_progress" ? 4 : 2} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={inputCls} />
                )}
              </div>
            ))}
            <div className="flex gap-2">
              <button data-testid="save-teacher-report-btn" onClick={save} disabled={busy}
                className="flex-1 bg-[#087F5B] hover:bg-[#066548] text-white font-semibold rounded-[10px] py-2.5 text-sm transition-colors duration-150 disabled:opacity-50">
                {form.id ? "Save changes" : "Save draft"}
              </button>
              <button data-testid="cancel-teacher-report-btn" onClick={() => setForm(null)}
                className="px-4 py-2.5 text-sm font-semibold text-[#667085] hover:text-[#132238] rounded-[10px] hover:bg-slate-50">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherReportPanel;
